import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { Box, Typography, Stack } from "@mui/material";

import ChangePaymentMethodSelect from "./ChangePaymentMethodSelect";
import ArrangementCardForm from "./ArrangementCardForm";
import ArrangementDirectDebitForm from "./ArrangementDirectDebitForm";
import ArrangementStandingOrder from "./ArrangementStandingOrder";
import ArrangementBankTransfer from "./ArrangementBankTransfer";
import ArrangementCheque from "./ArrangementCheque";
import ArrangementPostalOrder from "./ArrangementPostalOrder";

import { useArrangement } from "../../context/ArrangementProvider";


function ChangePaymentMethod({ account, handleComplete }) {

  const {
    paymentMethods,
    calculateStartDate,
    calculateEndDate,
    plan,
    setPlan,
    savePlanData,
  } = useArrangement();

  const [consent, setConsent] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [currentSortCode, setCurrentSortCode] = useState(null);
  const [currentAccountNumber, setCurrentAccountNumber] = useState(null);
  const [walletId, setWalletId] = useState(null);

  const [errorMessage, setErrorMessage] = useState(null);

  const SORT_CODE_LENGTH = 6;
  const ACCOUNT_NUMBER_LENGTH = 8;

  // Supported payment methods based on available form components
  const SUPPORTED_METHODS = {
    CARD: [5, 22],
    DIRECT_DEBIT: 11,
    STANDING_ORDER: 12,
    BANK_TRANSFER: 14,
    CHEQUE: 15,
    POSTAL_ORDER: 31
  };

  const getAllSupportedMethods = () => {
    return [...SUPPORTED_METHODS.CARD, SUPPORTED_METHODS.DIRECT_DEBIT, SUPPORTED_METHODS.STANDING_ORDER, 
            SUPPORTED_METHODS.BANK_TRANSFER, SUPPORTED_METHODS.CHEQUE, SUPPORTED_METHODS.POSTAL_ORDER];
  };

  useEffect(() => {
    // Set default selected method to existing method if supported, otherwise null
    if (plan?.paymentMethod) {
      let paymentMethod = Number.parseInt(plan.paymentMethod);
      if (!getAllSupportedMethods().includes(paymentMethod)) {
        paymentMethod = null; // Set to null for unsupported methods
      }
      setSelectedMethod(paymentMethod);
      setConsent(false); // Reset consent on page load
      // Hold the existing wallet ID
      setWalletId(plan?.walletId);
      // Set existing account details
      setCurrentSortCode(plan?.sortCode);
      setCurrentAccountNumber(plan?.accountNumber);
    } else {
      setSelectedMethod(11);
    }
  }, [plan]);

  const savePaymentMethod = async () => {
    // Triggered when Continue is pressed in step 1
    // Validate sort code and account number
    if (selectedMethod === paymentMethods["UK DIRECT DEBIT"]) {
      if (currentSortCode === null || currentSortCode.length < SORT_CODE_LENGTH) {
        setErrorMessage(`Sort code must contain ${SORT_CODE_LENGTH} numbers.`);
        return;
      } else if (currentAccountNumber === null || currentAccountNumber.length < ACCOUNT_NUMBER_LENGTH) {
        setErrorMessage(`Account number must contain ${ACCOUNT_NUMBER_LENGTH} numbers.`);
        return;
      };
    };

    // Calculate new dates based on selected payment method
    const newStartDate = calculateStartDate(
      selectedMethod,
      plan?.paymentFrequency,
      plan?.monthlyDay,
      plan?.weeklyDay
    );
    const newEndDate = calculateEndDate(
      newStartDate,
      plan?.paymentFrequency,
      plan?.numberOfInstalments,
    );

    // Update bank account details before saving
    const newPlan = {
      ...plan,
      sortCode: currentSortCode,
      accountNumber: currentAccountNumber,
      walletId: selectedMethod === paymentMethods["UK DIRECT DEBIT"] && walletId ? walletId : null,
      dateCreated: newStartDate,
      nextPaymentDate: newStartDate,
      endDate: newEndDate,
      paymentMethod: selectedMethod,
    }

    setPlan(newPlan);
    setErrorMessage(null);

    const success = await savePlanData(newPlan);

    handleComplete(success);
  };

  return (
    <>
      <Typography fontWeight={"bold"}>
        Change your payment method
      </Typography>
      <Stack
        sx={{
          mt: 2,
        }}
        spacing={2}
        direction={{
          xs: "column",
          md: "row",
        }}
      >
        <Box sx={{ width: { xs: "100%", md: "30%" } }}>
          {/* Left column */}
          <ChangePaymentMethodSelect
            account={account}
            consent={consent}
            setConsent={setConsent}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            setCurrentSortCode={setCurrentSortCode}
            setCurrentAccountNumber={setCurrentAccountNumber}
            selectedMethod={selectedMethod}
            setSelectedMethod={setSelectedMethod}
            savePaymentMethod={savePaymentMethod}
          />
          {/* End of left column */}
        </Box>
        <Box sx={{ width: { xs: "100%", md: "70%" } }}>
          {/* Right column */}
          {selectedMethod === null && (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography>Please select a payment method from the list</Typography>
            </Box>
          )}
          {SUPPORTED_METHODS.CARD.includes(selectedMethod) && <ArrangementCardForm
            consent={consent} setConsent={setConsent}
          />}
          {selectedMethod === SUPPORTED_METHODS.DIRECT_DEBIT && <ArrangementDirectDebitForm
            consent={consent}
            setConsent={setConsent}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            currentSortCode={currentSortCode}
            setCurrentSortCode={setCurrentSortCode}
            currentAccountNumber={currentAccountNumber}
            setCurrentAccountNumber={setCurrentAccountNumber}
          />}
          {selectedMethod === SUPPORTED_METHODS.STANDING_ORDER && <ArrangementStandingOrder
            account={account} setConsent={setConsent}
          />}
          {selectedMethod === SUPPORTED_METHODS.BANK_TRANSFER && <ArrangementBankTransfer
            account={account} setConsent={setConsent}
          />}
          {selectedMethod === SUPPORTED_METHODS.CHEQUE && <ArrangementCheque
            account={account} setConsent={setConsent}
          />}
          {selectedMethod === SUPPORTED_METHODS.POSTAL_ORDER && <ArrangementPostalOrder
            account={account} setConsent={setConsent}
          />}
          {/* End of right column */}
        </Box>
      </Stack>
    </>
  )
};

ChangePaymentMethod.propTypes = {
  account: PropTypes.object.isRequired,
  handleComplete: PropTypes.func.isRequired,
};

export default ChangePaymentMethod;